import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { logout, loginSuccess } from "../../features/auth/authSlice";
import { clearPermissions } from "../../features/permissions/permissionSlice";
import { changePasswordRequest, logoutRequest, updateSessionPreferenceRequest, toggleSaleDiscountRequest, toggleYarnCartonModeRequest, toggleWarpColourFilterRequest, toggleQrHideDealerEncodedPriceRequest, toggleWhatsAppAutoRequest, getDefaultLocationRequest } from "../../api/authAPI";
import { getAccessToken, getSessionExpiry } from "../../Util/authStorage.js";
import { readKbPref, writeKbPref } from "../../Util/kbPrefStorage.js";
import PageToolbar from "../../Components/PageToolbar.js";
import FormCard from "../../Components/FormCard.js";
import FormField from "../../Components/FormField.js";
import FormActions from "../../Components/FormActions.js";
import DefaultLocationSelect from "../../Components/DefaultLocationSelect.js";
import DashboardScopeCard from "./DashboardScopeCard.js";
import useDefaultLocation from "../../hooks/useDefaultLocation.js";
import catchAxiosError from "../../Util/catchAxiosError.js";
import styles from "./ChangePassword.module.css";
import goBack from "../../Util/goBack.js";

const SESSION_OPTS = [
    { label: "5 min.",  value: 300   },
    { label: "15 min.", value: 900   },
    { label: "30 min.", value: 1800  },
    { label: "1 hr",     value: 3600  },
    { label: "2 hrs",    value: 7200  },
    { label: "6 hrs",    value: 21600 },
    { label: "12 hrs",   value: 43200 },
    { label: "24 hrs",   value: 86400 },
];

/** Reusable password field with eye toggle */
function PwdField({ id, label, value, onChange, placeholder, disabled }) {
    const [show, setShow] = useState(false);
    return (
        <FormField label={label}>
            <div className={styles.pwdWrap}>
                <input
                    id={id}
                    type={show ? "text" : "password"}
                    className={styles.input}
                    placeholder={placeholder ?? "••••••••"}
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                    required
                    autoComplete="new-password"
                />
                <button
                    type="button"
                    className={styles.eyeBtn}
                    onClick={() => setShow((v) => !v)}
                    tabIndex={-1}
                    aria-label={show ? "Hide" : "Show"}
                >
                    {show ? (
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                    ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                    )}
                </button>
            </div>
        </FormField>
    );
}

function ChangePassword() {
    const dispatch = useDispatch();
    const history  = useHistory();

    const user = useSelector((s) => s.auth.user);
    const userDefaultSecs = user?.default_session_secs ?? 1800;
    const isAdmin = user?.roles?.includes("Admin") || user?.username === "ADMIN";

    // Default location — the server lists only the locations this user may
    // use (their assigned ones; all active ones when unscoped).
    const { defaultLocationId, setDefaultLocation, patchDefaultLocation } = useDefaultLocation();
    const [locationOptions, setLocationOptions] = useState([]);
    const [locationsLoading, setLocationsLoading] = useState(true);
    useEffect(() => {
        const controller = new AbortController();
        getDefaultLocationRequest(controller.signal)
            .then((d) => {
                setLocationOptions(d?.locations ?? []);
                // Server is authoritative (e.g. an admin changed it since login).
                if ((d?.defaultLocationId ?? null) !== defaultLocationId) {
                    patchDefaultLocation(d?.defaultLocationId ?? null);
                }
            })
            .catch((err) => { if (!controller.signal.aborted) catchAxiosError(err); })
            .finally(() => { if (!controller.signal.aborted) setLocationsLoading(false); });
        return () => controller.abort();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const [current, setCurrent]   = useState("");
    const [next,    setNext]      = useState("");
    const [confirm, setConfirm]   = useState("");
    const [load,    setLoad]      = useState(false);

    // Session preference
    const sessionIdx = SESSION_OPTS.findIndex((o) => o.value === userDefaultSecs);
    const [prefIdx,  setPrefIdx]  = useState(sessionIdx >= 0 ? sessionIdx : 2);
    const [prefLoad, setPrefLoad] = useState(false);

    // Sale discount toggle (admin only)
    const [saleDiscountEnabled, setSaleDiscountEnabled] = useState(user?.saleDiscountEnabled ?? true);
    const [sdLoad, setSdLoad] = useState(false);

    // Yarn carton mode toggle (admin only)
    const [yarnCartonModeEnabled, setYarnCartonModeEnabled] = useState(user?.yarnCartonModeEnabled ?? false);
    const [ycLoad, setYcLoad] = useState(false);

    // Warp colour filter toggle (admin only)
    const [warpColourFilterEnabled, setWarpColourFilterEnabled] = useState(user?.warpColourFilterEnabled ?? false);
    const [wcLoad, setWcLoad] = useState(false);

    // QR label: hide encoded price when dealer rate ≠ sale price (admin only)
    const [qrHideDealerPrice, setQrHideDealerPrice] = useState(user?.qrHideDealerEncodedPriceEnabled ?? true);
    const [qhLoad, setQhLoad] = useState(false);

    // Automatic WhatsApp: 12 AM invoice job + LR-approved message (admin only)
    const [waAutoInvoice, setWaAutoInvoice] = useState(user?.whatsappAutoInvoiceEnabled ?? false);
    const [waAutoLr, setWaAutoLr] = useState(user?.whatsappAutoLrEnabled ?? false);
    const [waAutoReminders, setWaAutoReminders] = useState(user?.paymentRemindersAutoEnabled ?? false);
    const [waAdminNumber, setWaAdminNumber] = useState(user?.whatsappAdminNumber || "919825114256");
    const [waLoad, setWaLoad] = useState(false);

    // On-screen keyboard preference (local-only, no backend call)
    const [kbPref, setKbPrefState] = useState(readKbPref);

    const validate = () => {
        if (!current || !next || !confirm) {
            toast.error("All fields are required.");
            return false;
        }
        if (next.length < 4) {
            toast.error("New password must be at least 4 characters.");
            return false;
        }
        if (next !== confirm) {
            toast.error("New password and confirmation don't match.");
            return false;
        }
        if (current === next) {
            toast.error("New password must be different from current password.");
            return false;
        }
        return true;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setLoad(true);
        const toastId = toast.loading("Changing password…");

        try {
            await changePasswordRequest(current, next);

            // Revoke the refresh token & clear local session
            await logoutRequest();
            dispatch(logout());
            dispatch(clearPermissions());

            toast.dismiss(toastId);
            toast.success("Password changed. Please log in with your new password.");
            history.replace("/login");
        } catch (err) {
            toast.dismiss(toastId);
            toast.error(err?.message ?? "Failed to change password.");
        } finally {
            setLoad(false);
        }
    };

    return (
        <div className="container-fluid pt-3 pb-4 px-3 px-md-4">
            <PageToolbar title="Change Password" onBack={() => goBack(history)} />

            <form className="form-column form-column--narrow" onSubmit={onSubmit} autoComplete="off">
                <FormCard maxWidth={480}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>

                        <PwdField
                            id="current-password"
                            label="Current Password *"
                            value={current}
                            onChange={(e) => setCurrent(e.target.value)}
                            disabled={load}
                            placeholder="Your current password"
                        />

                        <PwdField
                            id="new-password"
                            label="New Password *"
                            value={next}
                            onChange={(e) => setNext(e.target.value)}
                            disabled={load}
                            placeholder="At least 4 characters"
                        />

                        <PwdField
                            id="confirm-password"
                            label="Confirm New Password *"
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            disabled={load}
                            placeholder="Repeat new password"
                        />

                        {/* Visual strength hint */}
                        {next.length > 0 && (
                            <div className={styles.strengthBar}>
                                <div
                                    className={styles.strengthFill}
                                    style={{
                                        width: `${Math.min(100, (next.length / 12) * 100)}%`,
                                        background:
                                            next.length < 6 ? "#e05f4a"
                                            : next.length < 10 ? "#f0a500"
                                            : "#2ecc71",
                                    }}
                                />
                            </div>
                        )}

                    </div>
                </FormCard>
                <FormActions
                    onCancel={() => goBack(history)}
                    isLoading={load}
                    saveLabel="Change Password"
                />
            </form>

            {/* ── Sale Discount Toggle (Admin only) ── */}
            {isAdmin && (
                <FormCard maxWidth={480} style={{ marginTop: "1.5rem" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                        <div>
                            <p className={styles.prefLabel}>Sale Discount Period</p>
                            <p className={styles.prefHint}>
                                When enabled, the sale discount column is visible across all sales bill and challan pages.
                            </p>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                            <label className={styles.toggle}>
                                <input
                                    type="checkbox"
                                    checked={saleDiscountEnabled}
                                    onChange={(e) => setSaleDiscountEnabled(e.target.checked)}
                                    disabled={sdLoad}
                                />
                                <span className={styles.toggleSlider} />
                            </label>
                            <span style={{ fontSize: "var(--fs-body)", fontWeight: 500 }}>
                                {saleDiscountEnabled ? "Sale Discount is ON" : "Sale Discount is OFF"}
                            </span>
                        </div>

                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                            <button
                                type="button"
                                className={styles.saveBtn}
                                disabled={sdLoad}
                                onClick={async () => {
                                    setSdLoad(true);
                                    try {
                                        await toggleSaleDiscountRequest(saleDiscountEnabled);
                                        dispatch(loginSuccess({
                                            token: getAccessToken(),
                                            refreshExpiresAt: getSessionExpiry(),
                                            user: { ...user, saleDiscountEnabled },
                                        }));
                                        toast.success(`Sale discount ${saleDiscountEnabled ? "enabled" : "disabled"}.`);
                                    } catch (err) {
                                        toast.error(err?.message ?? "Failed to update setting.");
                                    } finally {
                                        setSdLoad(false);
                                    }
                                }}
                            >
                                {sdLoad ? "Saving…" : "Save Setting"}
                            </button>
                        </div>
                    </div>
                </FormCard>
            )}

            {/* ── Yarn Carton Mode Toggle (Admin only) ── */}
            {isAdmin && (
                <FormCard maxWidth={480} style={{ marginTop: "1.5rem" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                        <div>
                            <p className={styles.prefLabel}>Yarn Carton Stock Mode</p>
                            <p className={styles.prefHint}>
                                When enabled, yarn stock is tracked by carton count (not weight): purchase challans
                                and consumption capture total cartons, and the day-end carton summary becomes available.
                            </p>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                            <label className={styles.toggle}>
                                <input
                                    type="checkbox"
                                    checked={yarnCartonModeEnabled}
                                    onChange={(e) => setYarnCartonModeEnabled(e.target.checked)}
                                    disabled={ycLoad}
                                />
                                <span className={styles.toggleSlider} />
                            </label>
                            <span style={{ fontSize: "var(--fs-body)", fontWeight: 500 }}>
                                {yarnCartonModeEnabled ? "Carton Mode is ON" : "Carton Mode is OFF"}
                            </span>
                        </div>

                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                            <button
                                type="button"
                                className={styles.saveBtn}
                                disabled={ycLoad}
                                onClick={async () => {
                                    setYcLoad(true);
                                    try {
                                        await toggleYarnCartonModeRequest(yarnCartonModeEnabled);
                                        dispatch(loginSuccess({
                                            token: getAccessToken(),
                                            refreshExpiresAt: getSessionExpiry(),
                                            user: { ...user, yarnCartonModeEnabled },
                                        }));
                                        toast.success(`Yarn carton mode ${yarnCartonModeEnabled ? "enabled" : "disabled"}.`);
                                    } catch (err) {
                                        toast.error(err?.message ?? "Failed to update setting.");
                                    } finally {
                                        setYcLoad(false);
                                    }
                                }}
                            >
                                {ycLoad ? "Saving…" : "Save Setting"}
                            </button>
                        </div>
                    </div>
                </FormCard>
            )}

            {/* ── Warp Colour Filter Toggle (Admin only) ── */}
            {isAdmin && (
                <FormCard maxWidth={480} style={{ marginTop: "1.5rem" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                        <div>
                            <p className={styles.prefLabel}>Warp Colour Machine Filter</p>
                            <p className={styles.prefHint}>
                                When enabled, machine program allocation filters candidate machines only by the
                                matching&apos;s warp colour. A matching with no warp colour set matches no machines.
                                When disabled, the previous colour-mapping filter is used.
                            </p>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                            <label className={styles.toggle}>
                                <input
                                    type="checkbox"
                                    checked={warpColourFilterEnabled}
                                    onChange={(e) => setWarpColourFilterEnabled(e.target.checked)}
                                    disabled={wcLoad}
                                />
                                <span className={styles.toggleSlider} />
                            </label>
                            <span style={{ fontSize: "var(--fs-body)", fontWeight: 500 }}>
                                {warpColourFilterEnabled ? "Warp Colour Filter is ON" : "Warp Colour Filter is OFF"}
                            </span>
                        </div>

                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                            <button
                                type="button"
                                className={styles.saveBtn}
                                disabled={wcLoad}
                                onClick={async () => {
                                    setWcLoad(true);
                                    try {
                                        await toggleWarpColourFilterRequest(warpColourFilterEnabled);
                                        dispatch(loginSuccess({
                                            token: getAccessToken(),
                                            refreshExpiresAt: getSessionExpiry(),
                                            user: { ...user, warpColourFilterEnabled },
                                        }));
                                        toast.success(`Warp colour filter ${warpColourFilterEnabled ? "enabled" : "disabled"}.`);
                                    } catch (err) {
                                        toast.error(err?.message ?? "Failed to update setting.");
                                    } finally {
                                        setWcLoad(false);
                                    }
                                }}
                            >
                                {wcLoad ? "Saving…" : "Save Setting"}
                            </button>
                        </div>
                    </div>
                </FormCard>
            )}

            {/* ── QR Dealer Price Toggle (Admin only) ── */}
            {isAdmin && (
                <FormCard maxWidth={480} style={{ marginTop: "1.5rem" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                        <div>
                            <p className={styles.prefLabel}>Hide QR Price for Dealer-Rate Designs</p>
                            <p className={styles.prefHint}>
                                When enabled, QR labels omit the encoded price for catalogues whose dealer rate
                                differs from the sale price.
                            </p>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                            <label className={styles.toggle}>
                                <input
                                    type="checkbox"
                                    checked={qrHideDealerPrice}
                                    onChange={(e) => setQrHideDealerPrice(e.target.checked)}
                                    disabled={qhLoad}
                                />
                                <span className={styles.toggleSlider} />
                            </label>
                            <span style={{ fontSize: "var(--fs-body)", fontWeight: 500 }}>
                                {qrHideDealerPrice ? "Hide Dealer QR Price is ON" : "Hide Dealer QR Price is OFF"}
                            </span>
                        </div>

                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                            <button
                                type="button"
                                className={styles.saveBtn}
                                disabled={qhLoad}
                                onClick={async () => {
                                    setQhLoad(true);
                                    try {
                                        await toggleQrHideDealerEncodedPriceRequest(qrHideDealerPrice);
                                        dispatch(loginSuccess({
                                            token: getAccessToken(),
                                            refreshExpiresAt: getSessionExpiry(),
                                            user: { ...user, qrHideDealerEncodedPriceEnabled: qrHideDealerPrice },
                                        }));
                                        toast.success(`Hide dealer QR price ${qrHideDealerPrice ? "enabled" : "disabled"}.`);
                                    } catch (err) {
                                        toast.error(err?.message ?? "Failed to update setting.");
                                    } finally {
                                        setQhLoad(false);
                                    }
                                }}
                            >
                                {qhLoad ? "Saving…" : "Save Setting"}
                            </button>
                        </div>
                    </div>
                </FormCard>
            )}

            {/* ── Automatic WhatsApp (Admin only) ── */}
            {isAdmin && (
                <FormCard maxWidth={480} style={{ marginTop: "1.5rem" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                        <div>
                            <p className={styles.prefLabel}>Automatic WhatsApp</p>
                            <p className={styles.prefHint}>
                                Sends to each party and its agent (leading and accountant phone) without anyone
                                clicking WhatsApp. Messages go one at a time, and no number gets the same bill twice.
                            </p>
                        </div>

                        <div>
                            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                                <label className={styles.toggle}>
                                    <input
                                        type="checkbox"
                                        checked={waAutoInvoice}
                                        onChange={(e) => setWaAutoInvoice(e.target.checked)}
                                        disabled={waLoad}
                                    />
                                    <span className={styles.toggleSlider} />
                                </label>
                                <span style={{ fontSize: "var(--fs-body)", fontWeight: 500 }}>
                                    {waAutoInvoice ? "12 AM invoices are ON" : "12 AM invoices are OFF"}
                                </span>
                            </div>
                            <p className={styles.prefHint} style={{ marginTop: "0.35rem" }}>
                                Every night at 12 AM, the day&apos;s sales bills (PDF) go to the party and agent.
                            </p>
                        </div>

                        <div>
                            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                                <label className={styles.toggle}>
                                    <input
                                        type="checkbox"
                                        checked={waAutoLr}
                                        onChange={(e) => setWaAutoLr(e.target.checked)}
                                        disabled={waLoad}
                                    />
                                    <span className={styles.toggleSlider} />
                                </label>
                                <span style={{ fontSize: "var(--fs-body)", fontWeight: 500 }}>
                                    {waAutoLr ? "LR dispatch message is ON" : "LR dispatch message is OFF"}
                                </span>
                            </div>
                            <p className={styles.prefHint} style={{ marginTop: "0.35rem" }}>
                                When transport receipts are approved, the dispatch details and LR photos go to the party and agent.
                            </p>
                        </div>

                        <div>
                            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                                <label className={styles.toggle}>
                                    <input
                                        type="checkbox"
                                        checked={waAutoReminders}
                                        onChange={(e) => setWaAutoReminders(e.target.checked)}
                                        disabled={waLoad}
                                    />
                                    <span className={styles.toggleSlider} />
                                </label>
                                <span style={{ fontSize: "var(--fs-body)", fontWeight: 500 }}>
                                    {waAutoReminders ? "Payment reminders are ON" : "Payment reminders are OFF"}
                                </span>
                            </div>
                            <p className={styles.prefHint} style={{ marginTop: "0.35rem" }}>
                                Mon–Sat at 11:00, parties past a reminder tier get their outstanding statement (rules on
                                Payment Reminders). While off, nothing is sent automatically; a rule can still be run by hand.
                            </p>
                        </div>

                        <div>
                            <label htmlFor="wa-admin-number" style={{ fontSize: "var(--fs-body)", fontWeight: 500 }}>
                                Admin number for missing contacts
                            </label>
                            <input
                                id="wa-admin-number"
                                type="tel"
                                inputMode="numeric"
                                className="form-control"
                                style={{ marginTop: "0.35rem", maxWidth: 220 }}
                                value={waAdminNumber}
                                onChange={(e) => setWaAdminNumber(e.target.value.replace(/[^\d+ ]/g, ""))}
                                disabled={waLoad}
                                placeholder="919825114256"
                            />
                            <p className={styles.prefHint} style={{ marginTop: "0.35rem" }}>
                                When a party or agent has no number saved, this number gets one summary listing them
                                and their bills.
                            </p>
                        </div>

                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                            <button
                                type="button"
                                className={styles.saveBtn}
                                disabled={waLoad}
                                onClick={async () => {
                                    setWaLoad(true);
                                    try {
                                        const saved = await toggleWhatsAppAutoRequest({ autoInvoice: waAutoInvoice, autoLr: waAutoLr, autoReminders: waAutoReminders, adminNumber: waAdminNumber });
                                        const adminNumber = saved?.whatsappAdminNumber || waAdminNumber;
                                        setWaAdminNumber(adminNumber);
                                        dispatch(loginSuccess({
                                            token: getAccessToken(),
                                            refreshExpiresAt: getSessionExpiry(),
                                            user: { ...user, whatsappAutoInvoiceEnabled: waAutoInvoice, whatsappAutoLrEnabled: waAutoLr, paymentRemindersAutoEnabled: waAutoReminders, whatsappAdminNumber: adminNumber },
                                        }));
                                        toast.success(`Automatic WhatsApp saved: 12 AM invoices ${waAutoInvoice ? "on" : "off"}, LR message ${waAutoLr ? "on" : "off"}, payment reminders ${waAutoReminders ? "on" : "off"}.`);
                                    } catch (err) {
                                        toast.error(err?.message ?? "Failed to update setting.");
                                    } finally {
                                        setWaLoad(false);
                                    }
                                }}
                            >
                                {waLoad ? "Saving…" : "Save Setting"}
                            </button>
                        </div>
                    </div>
                </FormCard>
            )}

            {/* ── Dashboard firm/unit scope (Admin only) ── */}
            {isAdmin && <DashboardScopeCard />}

            {/* ── Session Preference ── */}
            <FormCard maxWidth={480} style={{ marginTop: "1.5rem" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                    <div>
                        <p className={styles.prefLabel}>Default Login Session Duration</p>
                        <p className={styles.prefHint}>
                            How long you stay logged in after each sign-in.
                        </p>
                    </div>

                    <div className={styles.sliderWrap}>
                        <input
                            type="range"
                            min={0}
                            max={SESSION_OPTS.length - 1}
                            step={1}
                            value={prefIdx}
                            onChange={(e) => setPrefIdx(Number(e.target.value))}
                            className={styles.slider}
                            disabled={prefLoad}
                            style={{ "--pct": `${(prefIdx / (SESSION_OPTS.length - 1)) * 100}%` }}
                        />
                        <div className={styles.sliderTicks}>
                            {SESSION_OPTS.map((o, i) => (
                                <span
                                    key={o.value}
                                    className={`${styles.sliderTick} ${i === prefIdx ? styles.sliderTickActive : ""}`}
                                >
                                    {o.label}
                                </span>
                            ))}
                        </div>
                    </div>

                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        <button
                            type="button"
                            className={styles.saveBtn}
                            disabled={prefLoad}
                            onClick={async () => {
                                setPrefLoad(true);
                                try {
                                    await updateSessionPreferenceRequest(SESSION_OPTS[prefIdx].value);
                                    toast.success(`Session duration set to ${SESSION_OPTS[prefIdx].label}.`);
                                } catch (err) {
                                    toast.error(err?.message ?? "Failed to save preference.");
                                } finally {
                                    setPrefLoad(false);
                                }
                            }}
                        >
                            {prefLoad ? "Saving…" : "Save Preference"}
                        </button>
                    </div>
                </div>
            </FormCard>

            {/* ── Default Location ── */}
            <FormCard maxWidth={480} style={{ marginTop: "1.5rem" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                    <div>
                        <p className={styles.prefLabel}>Default Location</p>
                        <p className={styles.prefHint}>
                            Pre-selected wherever a page asks for a location. You can still change it per entry.
                        </p>
                    </div>
                    <DefaultLocationSelect
                        allLocations={locationOptions}
                        value={defaultLocationId}
                        onChange={setDefaultLocation}
                        disabled={locationsLoading}
                    />
                </div>
            </FormCard>

            {/* ── On-Screen Keyboard Preference ── */}
            <FormCard maxWidth={480} style={{ marginTop: "1.5rem" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                    <div>
                        <p className={styles.prefLabel}>On-Screen Keyboard</p>
                        <p className={styles.prefHint}>
                            For devices where the phone's keyboard doesn't pop up automatically
                            (e.g. older Vivo phones), the app can show a built-in on-screen keyboard
                            inside dropdown selectors.
                        </p>
                    </div>

                    {[
                        { value: "auto", label: "Auto (recommended)", desc: "Detect automatically — show only when the phone's keyboard doesn't appear" },
                        { value: "on",   label: "Always show",        desc: "Always show the built-in keyboard in dropdown selectors" },
                        { value: "off",  label: "Never show",         desc: "Only use the phone's native keyboard" },
                    ].map((opt) => (
                        <label
                            key={opt.value}
                            style={{
                                display: "flex", alignItems: "flex-start", gap: "0.6rem",
                                cursor: "pointer", padding: "0.4rem 0",
                            }}
                        >
                            <input
                                type="radio"
                                name="kbPref"
                                value={opt.value}
                                checked={kbPref === opt.value}
                                onChange={() => {
                                    setKbPrefState(opt.value);
                                    writeKbPref(opt.value);
                                    toast.success(`Keyboard preference set to "${opt.label}".`);
                                }}
                                style={{ marginTop: 3 }}
                            />
                            <div>
                                <div style={{ fontSize: "var(--fs-body)", fontWeight: 500 }}>{opt.label}</div>
                                <div style={{ fontSize: "var(--fs-label)", color: "var(--ink-muted)" }}>{opt.desc}</div>
                            </div>
                        </label>
                    ))}
                </div>
            </FormCard>
        </div>
    );
}

export default ChangePassword;
